import React, { useState } from 'react';
import { DEALS } from '../../constants/columns';
import { useTable, useGlobalFilter, usePagination, useSortBy } from 'react-table';
import { dealsOptions } from '../../constants/filterOptions';
import { useLocation, useNavigate } from 'react-router-dom';
import { setFilters } from '../../utils/setFilters';
import useTableUrlSync, { useInitialTableQuery } from '../../hooks/useTableUrlState';
import { saveFilterSnapshot, clearFilterSnapshot, readFilterValues } from '../../utils/filterSnapshot';
import { sortDealsParams } from '../../constants/sortParams';

// components
import Header from '../../components/Header/Header';
import Table from '../../components/Table/Table';
import TableTabs from '../../components/TableTabs/TableTabs';
import Empty from '../../components/Empty/Empty';
import ClientPopup from '../../components/ClientPopup/ClientPopup';
import HookForm from '../../components/HookForm/HookForm';

// icons
import icon from '../../brand/generated/sidebar/deals.svg';
import axios from '../../plugins/axios';
import { PERMISSION_CAN_EDIT_DEALS, ROLE_ADMIN } from '../../constants/roles';
import isUserHaveRole from '../../utils/isUserHaveRole';
import DealAutos from './DealAutos';
import Modal from '../../components/Modal/Modal';
import ConfirmModal from '../../components/Modal/ConfirmModal';
import DealCarsModal from './DealCarsModal';
import bucket from '../../brand/generated/popover/delete.svg';
import RejectDealModal from './RejectDealModal';
import { socket } from '../../App';

export const FETCH_ALL_PAGESIZE = 20;
// const columns = DEALS;

const FILTER_NAMES = [...dealsOptions.map((option) => option.name), 'all'];

const Deals = ({ routerParams = {}, title = 'Сделки' }) => {
  const navigate = useNavigate();
  const location = useLocation();


  const [loading, setLoading] = useState(true);
  const tableStyle = { backgroundColor: 'white' };
  const [deals, setDeals] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const fromUrl = useInitialTableQuery(FILTER_NAMES);
  // Один компонент обслуживает несколько страниц (новые, БУ, неудачные сделки),
  // поэтому снимок фильтров у каждой свой.
  const snapshotKey = `deals${location.pathname}`;
  const [pagination, setPagination] = useState({
    page: fromUrl.page,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: fromUrl.pageSize || 10,
  });
  const [isFetchAll, setFetchAll] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [dealId, setDealId] = useState(null);
  const onCarClick = (id) => {
    setDealId(id);
    setModalOpen(true);
  };

  const [columns, setColumns] = useState(DEALS({ onCarClick }));

  const table = useTable(
    {
      columns,
      data: deals,
      initialState: {
        pageSize: pagination.pageSize,
      },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const { setGlobalFilter, page, setPageSize } = table;

  const [value, setValue] = useState(fromUrl.filters.all || '');
  const [queryParams, setQueryParams] = useState(fromUrl.filters);
  const filtersFormRef = React.useRef(null);
  useTableUrlSync({ queryParams, page: pagination.page, pageSize: pagination.pageSize });

  const applyFilters = (resetFilters) => {
    setFilters(resetFilters, setFetchAll, setPageSize, setPagination, setQueryParams, dealsOptions);

    if (resetFilters) {
      // Форма монтируется из defaultValue — без очистки восстановленные из адреса
      // значения вернулись бы при следующем открытии панели.
      dealsOptions.forEach((option) => delete option.defaultValue);
      clearFilterSnapshot(snapshotKey);
    } else {
      saveFilterSnapshot(snapshotKey, dealsOptions);
    }
  };

  // Фильтры из адреса: подписи к id берём из снимка последнего применения.
  React.useEffect(() => {
    const restored = readFilterValues(snapshotKey, fromUrl.filters);
    dealsOptions.forEach((option) => {
      const value = restored[option.name];
      if (value === undefined) return;
      option.value = value;
      option.defaultValue = value;
      filtersFormRef.current?.setValue(option.name, value);
    });
    // Только на монтировании: дальше значениями распоряжается форма.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addDeal = () => {
    navigate('/deals/create');
  };

  const handleNavigate = (index) => {
    navigate(`/deals/deal/${deals.find((item) => item.id === index).id}/main-info`, {
      state: deals.find((item) => item.id === index),
    });
  };

  const getDeals = (params) => {
    setLoading(true);
    axios
      .get('/api/crm/deals/getDeals', { params: { ...params, ...routerParams, _ts: Date.now() } })
      .then((res) => {
        setDeals(res.data.deals);
        setPagination({
          ...res.data.pagination,
          page: res.data.pagination?.pageNumber,
          pageSize: pagination.pageSize,
        });
        setDataCount(
          res.data.pagination?.count ??
            res.data.pagination?.totalCount ??
            res.data.pagination?.totalItems ??
            res.data.count ??
            0
        );
      })
      .catch((error) => {})
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    const onNavUpdate = () => {
      getDeals({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
    };

    socket.on('listenCrmNavigationNotifications', onNavUpdate);
    return () => {
      socket.off('listenCrmNavigationNotifications', onNavUpdate);
    };
  }, [pagination.page, pagination.pageSize, queryParams, routerParams]);

  React.useEffect(() => {
    if (!isFetchAll) {
      getDeals({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
    }
  }, [pagination.page, pagination.pageSize, queryParams]);
  React.useEffect(() => {
    getDeals({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
  }, [location]);

  

  const [showClientPopup, setShowClientPopup] = React.useState(false);
  const [clientId, setClientId] = React.useState(null);

  const sortBy = (value) => {
    if (!sortDealsParams.get(value)) return;
    if (sortDealsParams.get(value) === queryParams.sortBy) {
      queryParams.sortBy = '';
    } else {
      queryParams.sortBy = sortDealsParams.get(value);
    }
    setFetchAll(false);
    setQueryParams({ ...queryParams });
  };

  // Удаление сделки — только администратору и только после подтверждения.
  // Раньше пункт показывался всем, у кого в роли включено «Может удалять
  // сделки», и удалял сразу по клику, без вопроса.
  const [deleteDeal, setDeleteDeal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const onConfirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.post('/api/crm/deals/removeDeal', { id: deleteDeal?.id });
      setDeleteDeal(null);
      getDeals({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
    } finally {
      setDeleting(false);
    }
  };
  const popover = isUserHaveRole(ROLE_ADMIN)
    ? [
        {
          title: 'Удалить сделку',
          icon: bucket,
          onClick: (row) => setDeleteDeal(row),
        },
      ]
    : undefined;

  const onFastSearch = (value) => {
    setQueryParams({
      ...queryParams,
      all: value,
    })
  }

  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [rejectDealId, setRejectDealId] = useState(null);
  const onDropItemToTrash = (deal) => {
    console.log(deal);
    setRejectDealId(deal?.id)
    setOpenRejectModal(true);
  }
  const onDealRejected = () => {
    getDeals({ page: pagination.page, pageSize: pagination.pageSize, ...queryParams });
  }



  return (
    <>
      {showClientPopup && <ClientPopup isOpen={showClientPopup} setOpen={setShowClientPopup} clientId={clientId} />}
      <Header title={title} icon={icon} />
      <br />
      <br />
      <br />
      <br />
      <TableTabs
        setFilter={onFastSearch}
        value={value}
        filterComponent={
          <HookForm
            ref={filtersFormRef}
            inputs={dealsOptions}
            grid={{
              display: 'grid',
              gap: '10px',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
            }}
            style={{ backgroundColor: 'transparent' }}
          />
        }
        handleSearchParams={applyFilters}
        defaultFiltersOpen={Object.keys(fromUrl.filters).some((name) => name !== 'all')}
        setValue={setValue}
        navigate={addDeal}
        button={'Продать авто'}
        buttons={[
          {
            text: 'Продать доп.оборудование',
            onClick: () => navigate('/deals/equipment'),
          },
        ]}
        // permission={PERMISSION_CAN_EDIT_DEALS}
        inputPlaceholder={'Быстрый поиск по сделкам'}
      />
      <br />
      {!page.length && !loading ? (
        <Empty />
      ) : (
        <>
          <Table
            table={table}
            all={'сделок'}
            style={tableStyle}
            handleNavigate={handleNavigate}
            pagination
            sort
            setPagination={setPagination}
            setFetchAll={setFetchAll}
            isFetchAll={isFetchAll}
            setData={setDeals}
            p={pagination}
            setClientId={setClientId}
            setOpenClientPopup={setShowClientPopup}
            loading={loading}
            setLoading={setLoading}
            columns={columns}
            setColumns={setColumns}
            columnsSettingsKey="deals"
            queryParams={queryParams}
            api={'/api/crm/deals/getDeals'}
            response={'deals'}
            isSortable
            dataCount={dataCount}
            minWidth={1400}
            handleSortTable={sortBy}
            popover={popover}
            onDropItemToTrash={onDropItemToTrash}
          />
          <Modal
            isOpen={isModalOpen}
            onRequestClose={() => setModalOpen(false)}
            contentStyle={{
              height: 'max-content',
            }}
          >
            <DealCarsModal dealId={dealId} />
          </Modal>
        </>
      )}

      <RejectDealModal isOpen={openRejectModal} onClose={setOpenRejectModal} dealId={rejectDealId} onSuccess={onDealRejected}/>
      <ConfirmModal
        isOpen={!!deleteDeal}
        onClose={() => setDeleteDeal(null)}
        title="Удаление сделки"
        text={`Вы уверены, что хотите удалить сделку №${deleteDeal?.id ?? ''}? Отменить это будет нельзя.`}
        confirmText="Да"
        cancelText="Нет"
        loading={deleting}
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default Deals;
